import React, { useState, useEffect } from 'react';
import { useJobApplications } from '../../context/JobApplicationContext';
import { HiPlus, HiChartBarSquare, HiRectangleStack, HiExclamationTriangle } from 'react-icons/hi2';
import JobApplicationTable from '../JobApplicationTable/JobApplicationTable';
import JobApplicationForm from '../JobApplicationForm/JobApplicationForm';
import Analytics from '../Analytics/Analytics';
import FollowUpPanel from '../FollowUpPanel/FollowUpPanel';
import FilterPanel from '../FilterPanel/FilterPanel';
import ActivityHeatmap from '../ActivityHeatmap/ActivityHeatmap';
import styles from './Dashboard.module.css';

const Dashboard = () => {
  const {
    applications,
    analytics,
    followUps,
    loading,
    error,
    filters,
    fetchApplications,
    fetchAnalytics,
    fetchFollowUps,
    clearError
  } = useJobApplications();

  const [showForm, setShowForm] = useState(false);
  const [editingApplication, setEditingApplication] = useState(null);
  const [activeTab, setActiveTab] = useState('applications');

  useEffect(() => {
    fetchApplications(filters);
    fetchAnalytics();
    fetchFollowUps();
  }, [fetchApplications, fetchAnalytics, fetchFollowUps, filters]);

  const handleCreateNew = () => {
    setEditingApplication(null);
    setShowForm(true);
  };

  const handleEdit = (application) => {
    setEditingApplication(application);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingApplication(null);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingApplication(null);
    fetchApplications(filters);
    fetchAnalytics();
    fetchFollowUps();
  };

  if (error) {
    return (
      <div className={styles.error}>
        <HiExclamationTriangle className={styles.errorIcon} />
        <h3>Something went wrong</h3>
        <p>{error}</p>
        <button onClick={clearError} className="btn btn-primary">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className={styles.dashboard}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.titleSection}>
            <h2 className={styles.title}>Dashboard</h2>
            <p className={styles.description}>Track and manage your job applications</p>
          </div>
          <button onClick={handleCreateNew} className="btn btn-primary btn-lg">
            <HiPlus />
            Add Application
          </button>
        </div>
        
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'applications' ? styles.active : ''}`}
            onClick={() => setActiveTab('applications')}
          >
            <HiRectangleStack className={styles.tabIcon} />
            Applications
            <span className={styles.tabCount}>{applications.length}</span>
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'analytics' ? styles.active : ''}`}
            onClick={() => setActiveTab('analytics')}
          >
            <HiChartBarSquare className={styles.tabIcon} />
            Analytics
          </button>
        </div>
      </div>

      {activeTab === 'applications' && (
        <div className={styles.applicationsView}>
          <div className={styles.sidebar}>
            <FilterPanel />
            <FollowUpPanel followUps={followUps} onEdit={handleEdit} />
          </div>
          
          <div className={styles.mainContent}>
            <JobApplicationTable
              applications={applications}
              loading={loading}
              onEdit={handleEdit}
            />
          </div>
        </div>
      )}

      {activeTab === 'analytics' && (
        <div className={styles.analyticsView}>
          <Analytics analytics={analytics} />
          <div className={styles.heatmapSection}>
            <div className={styles.heatmapHeader}>
              <h3>Activity Heatmap</h3>
              <p className={styles.heatmapDescription}>
                Your application activity over the past year
              </p>
            </div>
            <ActivityHeatmap data={analytics?.heatmap || []} />
          </div>
        </div>
      )}

      {showForm && (
        <JobApplicationForm
          application={editingApplication}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );
};

export default Dashboard;